import type { ShortcutOs } from './keyboard-platform'
import { altKeyLabel, modKeyLabel } from './keyboard-platform'

export type ShortcutKeyToken = string

export interface ShortcutCatalogItem {
  id: string
  description: string
  mac: ShortcutKeyToken[]
  pc: ShortcutKeyToken[]
  /** Registered in code (may still fail in browser if OS reserves the combo). */
  wired: boolean
  note?: string
}

export interface ShortcutCatalogCategory {
  category: string
  items: ShortcutCatalogItem[]
}

/** Canonical list shown in the keyboard shortcuts dialog. */
export const KEYBOARD_SHORTCUTS_CATALOG: ShortcutCatalogCategory[] = [
  {
    category: 'Navigation',
    items: [
      {
        id: 'nav-back',
        description: 'Go back within tab',
        mac: [modKeyLabel('mac'), altKeyLabel('mac'), '\u2190'],
        pc: [modKeyLabel('pc'), altKeyLabel('pc'), '\u2190'],
        wired: true,
      },
      {
        id: 'nav-forward',
        description: 'Go forward within tab',
        mac: [modKeyLabel('mac'), altKeyLabel('mac'), '\u2192'],
        pc: [modKeyLabel('pc'), altKeyLabel('pc'), '\u2192'],
        wired: true,
      },
      {
        id: 'tab-prev',
        description: 'Previous tab',
        mac: [modKeyLabel('mac'), 'Shift', '['],
        pc: [modKeyLabel('pc'), 'Shift', '['],
        wired: true,
      },
      {
        id: 'tab-next',
        description: 'Next tab',
        mac: [modKeyLabel('mac'), 'Shift', ']'],
        pc: [modKeyLabel('pc'), 'Shift', ']'],
        wired: true,
      },
      {
        id: 'focus-sidebar',
        description: 'Focus sidebar',
        mac: [modKeyLabel('mac'), '1'],
        pc: [modKeyLabel('pc'), '1'],
        wired: true,
      },
      {
        id: 'focus-center',
        description: 'Focus center panel',
        mac: [modKeyLabel('mac'), '2'],
        pc: [modKeyLabel('pc'), '2'],
        wired: true,
      },
      {
        id: 'focus-agent',
        description: 'Focus agent pane',
        mac: [modKeyLabel('mac'), '3'],
        pc: [modKeyLabel('pc'), '3'],
        wired: true,
      },
    ],
  },
  {
    category: 'Tabs',
    items: [
      {
        id: 'tab-close-primary',
        description: 'Close current tab (recommended in Chrome)',
        mac: [modKeyLabel('mac'), altKeyLabel('mac'), 'W'],
        pc: [modKeyLabel('pc'), altKeyLabel('pc'), 'W'],
        wired: true,
      },
      {
        id: 'tab-close-fallback',
        description: 'Close current tab (if the browser delivers the shortcut)',
        mac: [modKeyLabel('mac'), 'W'],
        pc: [modKeyLabel('pc'), 'W'],
        wired: true,
        note: 'Often captured by the browser on Windows; prefer Ctrl+Alt+W.',
      },
      {
        id: 'tab-reopen',
        description: 'Reopen last closed tab',
        mac: [modKeyLabel('mac'), 'Shift', 'T'],
        pc: [modKeyLabel('pc'), 'Shift', 'T'],
        wired: true,
      },
      {
        id: 'tab-pin',
        description: 'Pin/unpin current tab',
        mac: [modKeyLabel('mac'), 'Shift', 'P'],
        pc: [modKeyLabel('pc'), altKeyLabel('pc'), 'P'],
        wired: true,
        note: 'Ctrl+Shift+P opens the command palette on Windows/Linux.',
      },
    ],
  },
  {
    category: 'Editor',
    items: [
      {
        id: 'editor-esc',
        description: 'Exit editor focus',
        mac: ['Esc'],
        pc: ['Esc'],
        wired: true,
      },
      {
        id: 'editor-save',
        description: 'Save document',
        mac: [modKeyLabel('mac'), 'S'],
        pc: [modKeyLabel('pc'), 'S'],
        wired: true,
      },
    ],
  },
  {
    category: 'Sidebar Navigation',
    items: [
      {
        id: 'sidebar-up-down',
        description: 'Navigate up/down in file browser',
        mac: ['\u2191', '\u2193'],
        pc: ['\u2191', '\u2193'],
        wired: true,
        note: 'When the sidebar tree is focused (Projects view).',
      },
      {
        id: 'sidebar-collapse',
        description: 'Close current folder',
        mac: ['\u2190'],
        pc: ['\u2190'],
        wired: true,
        note: 'When the sidebar tree is focused.',
      },
      {
        id: 'sidebar-expand',
        description: 'Open highlighted folder',
        mac: ['\u2192'],
        pc: ['\u2192'],
        wired: true,
        note: 'When the sidebar tree is focused.',
      },
      {
        id: 'sidebar-enter',
        description: 'Open file/folder in main window',
        mac: ['Enter'],
        pc: ['Enter'],
        wired: true,
        note: 'When the sidebar tree is focused.',
      },
      {
        id: 'sidebar-enter-new-tab',
        description: 'Open file/folder in new tab',
        mac: [modKeyLabel('mac'), 'Enter'],
        pc: [modKeyLabel('pc'), 'Enter'],
        wired: true,
        note: 'When the sidebar tree is focused.',
      },
    ],
  },
  {
    category: 'Click modifiers',
    items: [
      {
        id: 'click-new-tab',
        description: 'Open document or link in new tab',
        mac: [modKeyLabel('mac'), 'Click'],
        pc: [modKeyLabel('pc'), 'Click'],
        wired: true,
        note: 'Sidebar files, search results, internal links, and most document lists.',
      },
    ],
  },
  {
    category: 'General',
    items: [
      {
        id: 'palette-legacy',
        description: 'Open command palette (legacy)',
        mac: [modKeyLabel('mac'), 'K'],
        pc: [modKeyLabel('pc'), 'Shift', 'K'],
        wired: true,
        note: 'Mac shows a one-time note that the primary shortcut moved to P.',
      },
      {
        id: 'palette-primary',
        description: 'Open command palette',
        mac: [modKeyLabel('mac'), 'P'],
        pc: [modKeyLabel('pc'), 'Shift', 'P'],
        wired: true,
        note: 'Ctrl+P is reserved by the browser (Print).',
      },
      {
        id: 'search-view',
        description: 'Open search view',
        mac: [modKeyLabel('mac'), 'Shift', 'F'],
        pc: [modKeyLabel('pc'), 'Shift', 'F'],
        wired: true,
      },
      {
        id: 'list-find',
        description: 'Find in the current list',
        mac: [modKeyLabel('mac'), 'F'],
        pc: [modKeyLabel('pc'), 'F'],
        wired: true,
        note: 'Todos, Projects, and Activity. Also Search on the row context menu.',
      },
      {
        id: 'toggle-sidebar',
        description: 'Toggle sidebar',
        mac: [modKeyLabel('mac'), 'B'],
        pc: [modKeyLabel('pc'), 'B'],
        wired: true,
      },
      {
        id: 'toggle-agent',
        description: 'Toggle agent (right) pane',
        mac: [modKeyLabel('mac'), 'J'],
        pc: [modKeyLabel('pc'), 'J'],
        wired: true,
      },
      {
        id: 'shortcuts-dialog',
        description: 'This shortcuts dialog',
        mac: [modKeyLabel('mac'), 'Shift', '?'],
        pc: [modKeyLabel('pc'), 'Shift', '?'],
        wired: true,
      },
    ],
  },
]

export function keysForOs(item: ShortcutCatalogItem, os: ShortcutOs): ShortcutKeyToken[] {
  return os === 'mac' ? item.mac : item.pc
}
